/** Configuration options for infinite pagination queries */
interface InfinitePaginationOptions<TParams extends Record<string, unknown>> {
  /** Parameters to use in the query key */
  keyParams: TParams & { limit: number };

  /** Function to generate parameters for the API request based on the current page */
  queryFnParams: (pageParam: number) => TParams & { limit: number; skip: number };

  /** React Query infinite query options */
  opts: {
    initialPageParam: number;
    getNextPageParam: <TData extends { items: Array<unknown> }>(
      lastPage: TData,
      allPages: Array<TData>,
      lastPageParam: number,
      allPageParams: Array<number>,
    ) => number | undefined;
  };
}

/**
 * Creates a standardized configuration for infinite pagination queries This utility centralizes pagination logic for
 * consistent behavior across different infinite queries
 *
 * @param params - The base query parameters
 * @param defaultLimit - The default number of items per page (defaults to 50)
 * @returns Configuration object for infinite pagination queries
 */
export function createInfinitePaginationConfig<TParams extends Record<string, unknown>>(
  params: TParams,
  defaultLimit = 50,
): InfinitePaginationOptions<TParams> {
  const { limit = defaultLimit } = params ?? {};

  return {
    keyParams: {
      ...params,
      limit: limit as number,
    },
    queryFnParams: (pageParam) => {
      return {
        ...params,
        limit: limit as number,
        skip: (pageParam - 1) * (limit as number),
      };
    },
    opts: {
      initialPageParam: 1,
      getNextPageParam: <TData extends { items: Array<unknown> }>(
        lastPage: TData,
        _allPages: Array<TData>,
        lastPageParam: number,
        _allPageParams: Array<number>,
      ) => {
        if (lastPage.items.length < (limit as number)) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    },
  };
}
